import type { FocusEventHandler, PointerEventHandler } from 'react';
import {
  FilterRailOption,
  FilterRailOptionControl,
  FilterRailOptionCount,
  FilterRailOptionLabel,
  FilterRailOptionText,
} from '@vs-shop/commerce-ui/filterRail';
import { Checkbox } from '@vs-shop/ui/checkbox';

type Props = {
  // oxlint-disable-next-line vspoint-effect/branded-component-props -- presentational facet row key (taxon code / brand uuid / attribute value across rails), not a single entity id
  id: string;
  label: string;
  // Product count for the facet. Optional: the search rail supplies counts from
  // `/facets`; the category rail has none, so the badge is hidden when absent.
  count?: number | undefined;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
  onPointerEnter?: PointerEventHandler<HTMLLabelElement>;
  onPointerLeave?: PointerEventHandler<HTMLLabelElement>;
  onFocus?: FocusEventHandler<HTMLLabelElement>;
  onBlur?: FocusEventHandler<HTMLLabelElement>;
};

/**
 * rcprofi's arrangement of one selectable facet row, shared by every filter
 * dimension (taxon / brand / attribute / boolean) across the category, brand,
 * and search rails. Purely presentational — each dimension owns its own atom
 * subscription and hands this row the resolved `checked` state and a toggle
 * callback.
 *
 * The anatomy is `@vs-shop/commerce-ui`'s; what stays here is the composition:
 * a count badge composed only where there is a count to show, and rcprofi's
 * choice of the compact checkbox.
 */
export function FilterRow({
  id,
  label,
  count,
  checked,
  disabled = false,
  onCheckedChange,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
}: Props) {
  return (
    <FilterRailOption>
      <FilterRailOptionLabel
        htmlFor={id}
        data-disabled={disabled || undefined}
        className={disabled ? 'opacity-50' : undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <FilterRailOptionControl>
          <Checkbox
            variant="compact"
            aria-label={label}
            id={id}
            checked={checked}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
          />
          <FilterRailOptionText>{label}</FilterRailOptionText>
        </FilterRailOptionControl>
        {typeof count === 'number' ? <FilterRailOptionCount>{count}</FilterRailOptionCount> : null}
      </FilterRailOptionLabel>
    </FilterRailOption>
  );
}
